import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAttentionSummary } from '../../hooks/useAttentionSummary';
import { AttentionItem, AttentionSeverity } from '../../types/notification';

const severityColor = (severity: AttentionSeverity) => {
  switch (severity) {
    case 'OVERDUE':
      return 'error.main';
    case 'WARNING':
      return 'warning.main';
    default:
      return 'info.main';
  }
};

export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.company?.id;
  const { data } = useAttentionSummary(companyId);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const overdueCount = data?.overdueCount ?? 0;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (item: AttentionItem) => {
    handleClose();
    navigate(item.linkPath);
  };

  return (
    <>
      <Tooltip title={t('notifications.bellTooltip', 'Needs attention')}>
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label={t('notifications.bellAriaLabel', 'Notifications')}
          aria-controls={open ? 'notifications-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ ml: 1 }}
        >
          <Badge
            badgeContent={totalCount}
            color={overdueCount > 0 ? 'error' : 'warning'}
            max={99}
          >
            {totalCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'notifications-button', dense: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('notifications.title', 'Needs attention')}
          </Typography>
        </Box>
        <Divider />
        {items.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={t('notifications.empty', "You're all caught up")} />
          </MenuItem>
        )}
        {items.map((item, index) => (
          <MenuItem key={`${item.category}-${index}`} onClick={() => handleItemClick(item)}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: severityColor(item.severity),
                flexShrink: 0,
                mr: 1.5,
              }}
            />
            <ListItemText
              primary={item.title}
              secondary={item.subtitle}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
